import {
  Calendar,
  Clock,
  FileText,
  Phone,
  PhoneForwarded,
  Sparkles,
} from 'lucide-react';

import { IndustryPage, type IndustryPageConfig } from '@/components/marketing/industry-page';

const config: IndustryPageConfig = {
  seoTitle: 'AI Receptionist for Dental Practices — Answer Every Call, 24/7',
  seoDescription:
    'Never lose a new patient to voicemail again. Our AI receptionist answers every call to your dental practice, captures appointment requests, and answers FAQs — even mid-procedure.',
  path: '/industries/dental',
  badge: 'Dental Practices',
  headline: 'Every new patient call answered — even mid-procedure.',
  subheadline:
    'Your AI receptionist picks up on the first ring, answers FAQs, captures appointment requests, and transfers urgent calls to your team — 24 hours a day.',
  pains: [
    'New patients hang up when sent to voicemail and call your competitor instead.',
    'Front desk spends 40% of the day fielding routine questions about hours and insurance.',
    'After-hours calls go unanswered, losing weekend appointment requests.',
  ],
  features: [
    {
      icon: Phone,
      title: '24/7 call answering',
      desc: 'Every call answered on the first ring — before work, after hours, and during procedures when your team is hands-on.',
    },
    {
      icon: Calendar,
      title: 'Appointment request capture',
      desc: 'Collects patient name, contact number, and preferred appointment time. Drops a summary in your dashboard for your team to confirm.',
    },
    {
      icon: Sparkles,
      title: 'Answers from your knowledge base',
      desc: 'Hours, accepted insurance plans, new-patient process, parking — answered straight from your uploaded docs. No guessing.',
    },
    {
      icon: PhoneForwarded,
      title: 'Urgent call transfer',
      desc: 'Dental emergencies get warm-transferred to your on-call number immediately, with context already passed to your team.',
    },
    {
      icon: Clock,
      title: 'Sub-second response time',
      desc: 'Natural conversation with ~380ms first response. Patients feel like they\'re talking to your best front desk team member.',
    },
    {
      icon: FileText,
      title: 'Call transcripts & summaries',
      desc: 'Every call logged with an AI summary. Review missed calls and booking requests from one dashboard.',
    },
  ],
  faqs: [
    {
      q: 'Can it handle new patient intake questions?',
      a: 'Yes — upload your new patient FAQ, accepted insurance list, and fee guide. The agent answers from your content, accurately, every time.',
    },
    {
      q: 'What happens when a patient has a dental emergency?',
      a: 'You define the transfer rules in plain language. Emergencies get warm-transferred to your on-call number with a spoken handoff.',
    },
    {
      q: 'Does it integrate with my practice management software?',
      a: 'The agent captures booking requests and drops them into your dashboard. Direct calendar integration is on the roadmap.',
    },
    {
      q: 'How long does setup take?',
      a: 'Under 10 minutes. Upload your knowledge base, set your phone number, and your AI receptionist is live.',
    },
    {
      q: 'Can I use my existing phone number?',
      a: 'Yes — forward your existing number to the AI agent, or get a new local number. Either way works.',
    },
  ],
  ctaHeadline: 'Stop losing new patients to voicemail.',
  ctaSub: 'Set up your dental AI receptionist in under 10 minutes. No long-term contract.',
};

export function DentalIndustryPage(): JSX.Element {
  return <IndustryPage config={config} />;
}
